/**
 * useFolioPayments — TanStack Query hook over
 * `GET /organizations/{slug}/folios/{folioId}/payments` (SCRUM-338).
 *
 * The FOLIO-scoped payment log — the PII-safe read-model backing the payments
 * tab's Region B (`payments.payments.read`). Gateway rows ride this SAME log via
 * `processingRail:'gateway'` (the `/gateway-payments` endpoint is POST-only —
 * initiate/write, out of scope here).
 *
 * Mirrors `use-reservation-folio.ts`, BUT with a FOUR-branch status union
 * `loading | ready | forbidden | error` — NO `notFound` branch. `listPayments`
 * returns 200 + 403 ONLY: a missing folio 404s UPSTREAM in `useReservationFolio`
 * (there is no folioId to fetch), so a 404 here is unexpected and folds into the
 * generic `error` branch. An empty folio is `200 {data:[]}` → `ready` with an
 * empty array (honest-empty, NOT an error).
 *
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per CLAUDE.md
 *     non-negotiable §1)
 *   - `queryKey = [...bookingQueryKeys.folioPayments(folioId), orgSlug]`
 *   - `enabled: orgSlug !== null && folioId != null`
 *   - `retry: false`
 *   - the response envelope is `{ data: PaymentProjectionDto[] }` — the `ready`
 *     result unwraps `result.data.data`
 *   - every branch carries a `refetch` function
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { PaymentProjectionDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * 403 sentinel — the caller lacks `payments.payments.read`. The payments tab
 * renders an honest partial (the folio badge still shows via
 * `useReservationFolio`; the log region says "needs additional access"). Own
 * module-level Symbol, distinct from every other hook's `FORBIDDEN`.
 */
export const PAYMENTS_FORBIDDEN = Symbol('folio-payments:forbidden');

export type UseFolioPaymentsResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** The payment log, unwrapped from the `{ data }` envelope. */
      readonly data: PaymentProjectionDto[];
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly refetch: () => void;
    };

export function useFolioPayments(
  folioId: string | null,
): UseFolioPaymentsResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    // `folioPayments(id)` requires a string; when folioId is null the query is
    // disabled so the key is never used — an empty string keeps the tuple type
    // stable.
    queryKey: [...bookingQueryKeys.folioPayments(folioId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && folioId != null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/folios/{folioId}/payments',
        {
          params: {
            path: { slug: orgSlug as string, folioId: folioId as string },
          },
        },
      );
      if (result.response.status === 403) {
        return PAYMENTS_FORBIDDEN;
      }
      // NO 404 branch: listPayments returns 200 + 403 only. Any non-ok /
      // undefined-body status (including an unexpected 404) throws → `error`.
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/folios/${folioId}/payments failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || folioId == null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === PAYMENTS_FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }

  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    refetch,
  };
}
